// Interval math for a "coder A" vs "coder B" text diff — used to figure out,
// for any span of a paragraph's body text, whether both coders coded it the
// same way, differently, or only one of them touched it at all.
//   'both'  — both coders selected this text (see `conflict` for whether the
//             codes they applied differ).
//   'left'  — only the left-column coder selected this text.
//   'right' — only the right-column coder selected this text.
//   'none'  — neither coder touched this text (no highlight).

// Clips each excerpt to the paragraph's body-text range and converts to
// offsets relative to the start of that body text.
const excerptsToRanges = (excerpts, bodyStart, bodyEnd) =>
  excerpts
    .map((excerpt) => ({
      start: Math.max(excerpt.start, bodyStart) - bodyStart,
      end: Math.min(excerpt.end, bodyEnd) - bodyStart,
      codeIds: (excerpt.codings || []).map((c) => c.code_id),
    }))
    .filter((r) => r.end > r.start);

const sameIdSets = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

// Union of code ids across the ranges covering `point`, or null when no
// range covers it. Ranges are deliberately NOT pre-merged: offsets are
// end-exclusive, so two excerpts that merely touch at a boundary are
// independent, and where excerpts genuinely overlap their codes union only
// on the overlapped span — never on the non-overlapped tails.
const codeIdsCovering = (ranges, point) => {
  const covering = ranges.filter((r) => point >= r.start && point < r.end);
  if (!covering.length) return null;
  return [...new Set(covering.flatMap((r) => r.codeIds))];
};

const sameIdLists = (a, b) => sameIdSets(new Set(a), new Set(b));

// bodyText is a suffix of the paragraph's raw line (introText + bodyText = text),
// so its absolute start is the paragraph's end minus its own length.
// Offsets are UTF-16 code units (same convention as Excerpt#start/#end), so
// they line up 1:1 with JS string offsets.
export const buildParagraphDiff = (paragraph, leftExcerpts, rightExcerpts) => {
  const bodyText = paragraph.bodyText || '';
  const bodyStart = paragraph.end - bodyText.length;
  const bodyEnd = paragraph.end;

  const overlapsBody = (excerpt) => !(excerpt.end <= bodyStart || excerpt.start >= bodyEnd);

  const leftRanges = excerptsToRanges(leftExcerpts.filter(overlapsBody), bodyStart, bodyEnd);
  const rightRanges = excerptsToRanges(rightExcerpts.filter(overlapsBody), bodyStart, bodyEnd);

  // Segment at every excerpt boundary on either side, so each segment has a
  // single, per-span answer to "which codes cover this text" — boundaries
  // between back-to-back excerpts are preserved, not merged away.
  const points = new Set([0, bodyText.length]);
  leftRanges.forEach((r) => { points.add(r.start); points.add(r.end); });
  rightRanges.forEach((r) => { points.add(r.start); points.add(r.end); });
  const sortedPoints = [...points].sort((a, b) => a - b);

  const segments = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const start = sortedPoints[i];
    const end = sortedPoints[i + 1];
    if (start >= end) continue;

    const mid = (start + end) / 2;
    const leftCodeIds = codeIdsCovering(leftRanges, mid);
    const rightCodeIds = codeIdsCovering(rightRanges, mid);

    let status = 'none';
    let conflict = false;
    if (leftCodeIds && rightCodeIds) {
      status = 'both';
      conflict = !sameIdLists(leftCodeIds, rightCodeIds);
    } else if (leftCodeIds) {
      status = 'left';
    } else if (rightCodeIds) {
      status = 'right';
    }

    const prev = segments[segments.length - 1];
    if (
      prev && prev.end === start && prev.status === status && prev.conflict === conflict &&
      sameIdLists(prev.leftCodeIds, leftCodeIds || []) &&
      sameIdLists(prev.rightCodeIds, rightCodeIds || [])
    ) {
      // Adjacent segments that answer identically (same coverage, same
      // codes) coalesce back into one maximal span, so consumers (heatmap
      // cells, chip CSS) see clean bands rather than fragments.
      prev.end = end;
      continue;
    }

    segments.push({
      start,
      end,
      status,
      conflict,
      leftCodeIds: leftCodeIds || [],
      rightCodeIds: rightCodeIds || [],
    });
  }

  return { segments };
};

// Collapses a diff segment into one of the agreement states the comparison
// UI colors by (or null for uncoded text):
//   'agree'    — both coders, identical code sets.
//   'partial'  — both coders, overlapping but not identical code sets.
//   'conflict' — both coders, disjoint code sets.
//   'oneSided' — exactly one coder coded this text: coded-but-not-compared,
//                NOT a disagreement. Product decision: one coder coding what
//                the other skipped is fine — it renders neutral gray, stays
//                out of the disagreement walk, and matches the agreement
//                score, which already excludes single-coder units (see
//                InterCoderMapper.js and AiCoding::Scorer#comparable_units).
export const classifyDiffSegment = (segment) => {
  if (segment.status === 'both') {
    if (!segment.conflict) return 'agree';
    const shared = segment.leftCodeIds.some((id) => segment.rightCodeIds.includes(id));
    return shared ? 'partial' : 'conflict';
  }
  if (segment.status === 'left' || segment.status === 'right') return 'oneSided';
  return null;
};

// Payloads for the text-hover tooltip that explains a span's agreement
// state: one entry per coded diff segment, from a paragraph's already-
// computed buildParagraphDiff result. All four classifications emit — agree
// was originally excluded as needing no callout, but once every other state
// explained itself on hover, silence on green read as a bug rather than
// calm (per Alex), so it now gets its confirmation card too.
//
// start/end are ABSOLUTE character offsets — the same convention as the
// `start` attribute the renderer stamps on each .codeSpan — so a hover
// handler can resolve a hovered span to its segment by plain range lookup.
// (Spans never straddle segments; see the buildUnderlineSpans DOM contract.)
//
// sharedCodeIds: codes BOTH coders applied to this exact text — the two
// lists' intersection, nothing wider. A paragraph-grain version ("both
// applied somewhere in this paragraph") was tried and reverted: on a red
// span in a densely-coded paragraph it claimed "Both applied: A, B" while
// hiding that at THIS text one coder said A and the other said B — the very
// disagreement the span is red for (Alex's Temporal-rupture/Unbuffered-loss
// report on transcript 43890). Non-empty only on partial segments (conflict
// means disjoint); co-location elsewhere in the paragraph already shows up
// as that other text's own agree/partial coloring.
export const buildTooltipSegments = (paragraph, diff) => {
  const bodyText = paragraph.bodyText || '';
  const bodyStart = paragraph.end - bodyText.length;
  return (diff?.segments || [])
    .map((segment) => ({ segment, classification: classifyDiffSegment(segment) }))
    .filter(({ classification }) => classification)
    .map(({ segment, classification }) => ({
      start: bodyStart + segment.start,
      end: bodyStart + segment.end,
      classification,
      leftCodeIds: segment.leftCodeIds,
      rightCodeIds: segment.rightCodeIds,
      sharedCodeIds: segment.leftCodeIds.filter((id) => segment.rightCodeIds.includes(id)),
    }));
};

// Narrows an excerpts array (e.g. useTranscriptExcerpts' resolution) down to just one
// user's codings, dropping excerpts that end up with none.
export const filterCodingsToUser = (excerpts, userId) =>
  (excerpts || [])
    .map((excerpt) => ({ ...excerpt, codings: (excerpt.codings || []).filter((c) => c.user_id === userId) }))
    .filter((excerpt) => excerpt.codings.length > 0);

// Per-CODE chip verdicts for the chips listed under one paragraph, from the
// paragraph's buildParagraphDiff result. The chip row shows one chip per
// (column, code) — NOT per excerpt — so verdicts aggregate every span the
// column's coder applied the code to within the paragraph body.
//
// This is deliberately different from the per-SEGMENT classification the
// heatmap cells and underlines use: a segment where the coders' code sets
// overlap-but-differ is 'partial' as a *span*, but chip-wise each shared
// code is graded on its own merits and an unshared code over both-coded
// text IS the disagreement.
//
// A co-located chip composes from two components (per Alex, on the
// scenario gallery): an AGREE component — the coders applied the code to
// overlapping text (any non-empty intersection; overlap share is
// irrelevant, a span-fidelity Jaccard threshold was tried and dropped) —
// and a CONFLICT component — some stretch of the code, on EITHER side,
// sits on both-coded text where the other coder never applied it. Both
// components are symmetric, because the chip describes the CODE's story in
// this paragraph, not the coder's: a disagreement about the code demotes
// both columns' chips (green next to green/red for the same code read as a
// glitch). One-sided coverage (own text the other coder never touched) is
// NEVER a component: it's the gray underline's territory, so "they agree
// on it, just one person applied it to more" reads plain green, not
// green/red.
//
// Per (column, code):
//   'agree'    — agree component only: co-located somewhere, and no
//                stretch of the code (either side) is contested. Extra
//                unmatched codes on the same text do NOT demote a
//                co-located code, and neither does either coder's
//                one-sided coverage.
//   'partial'  — BOTH components: the code agrees on one stretch and
//                conflicts on another — green/red split chip in BOTH
//                columns, even the one whose own stretches all matched.
//   'conflict' — the other coder coded text intersecting this side's span,
//                yet never applied this code to ANY of the same text — this
//                code is the disagreement at that text, even if the other
//                coder reached for it elsewhere in the paragraph on their
//                own one-sided stretch (the 43889 case: disjoint spans of a
//                code grade per side, red here…).
//   (omitted)  — the code's span lies entirely in territory the other coder
//                left uncoded: coded-but-not-compared, not a verdict (same
//                one-sided product decision as classifyDiffSegment), so the
//                chip keeps its DEFAULT native styling (…and gray there in
//                the 43889 case). The disjoint case is thus the ONE place
//                the two columns' verdicts for a code differ — colored vs
//                default, never colored vs colored; co-located codes always
//                grade symmetrically.
// Returns [{ gridColumn, codeId, verdict }] with gridColumn 1 (left) or 3
// (right), matching the comparison grid's column indexes.
export const buildChipVerdicts = (diff) => {
  const segments = diff.segments || [];
  // codeId -> per-code span accounting across the whole paragraph: total
  // length each coder applied the code to, length of the intersection, and
  // whether an unmatched stretch of the code lies over both-coded text
  // (the 'conflict' trigger for each side).
  const stats = new Map();
  segments.forEach((segment) => {
    const length = segment.end - segment.start;
    new Set([...segment.leftCodeIds, ...segment.rightCodeIds]).forEach((codeId) => {
      const s = stats.get(codeId) ||
        { leftLen: 0, rightLen: 0, interLen: 0, leftContested: false, rightContested: false };
      const inLeft = segment.leftCodeIds.includes(codeId);
      const inRight = segment.rightCodeIds.includes(codeId);
      if (inLeft) s.leftLen += length;
      if (inRight) s.rightLen += length;
      if (inLeft && inRight) s.interLen += length;
      if (segment.status === 'both') {
        if (inLeft && !inRight) s.leftContested = true;
        if (inRight && !inLeft) s.rightContested = true;
      }
      stats.set(codeId, s);
    });
  });

  const verdicts = [];
  [
    { gridColumn: 1, ownLen: 'leftLen', contested: 'leftContested' },
    { gridColumn: 3, ownLen: 'rightLen', contested: 'rightContested' },
  ].forEach(({ gridColumn, ownLen, contested }) => {
    stats.forEach((s, codeId) => {
      if (!s[ownLen]) return; // this column's coder never applied the code
      let verdict = null;
      if (s.interLen) {
        // Co-located: symmetric — a contested stretch of this code on
        // EITHER side demotes both columns' chips to the split.
        verdict = (s.leftContested || s.rightContested) ? 'partial' : 'agree';
      } else if (s[contested]) {
        // No co-location at all (this includes the other coder applying the
        // code only on their own disjoint stretch): grade this side by its
        // own text — red iff some of it is both-coded without this code.
        verdict = 'conflict';
      }
      if (verdict) verdicts.push({ gridColumn, codeId, verdict });
    });
  });
  return verdicts;
};

// Chips buildChipVerdicts deliberately omits (coded-but-not-compared — the
// other coder never touched this code's text anywhere in the paragraph, so
// it's not a real agree/partial/conflict verdict) — but the comparison page
// still wants them visibly gray rather than wearing the code's own color,
// which reads as "this was compared and matched" when it wasn't. Derived
// the same way buildChipVerdicts derives its own per-column code coverage:
// union each column's codeIds straight off the diff's segments, minus
// whatever already got a real verdict. Pass the already-computed verdicts
// when you have them (the overlay cache does) to skip the rebuild.
export const buildOneSidedChipCodes = (diff, verdicts = null) => {
  const verdictCodesByColumn = { 1: new Set(), 3: new Set() };
  (verdicts || buildChipVerdicts(diff)).forEach(({ gridColumn, codeId }) => verdictCodesByColumn[gridColumn].add(codeId));
  const appliedByColumn = { 1: new Set(), 3: new Set() };
  (diff.segments || []).forEach((segment) => {
    segment.leftCodeIds.forEach((id) => appliedByColumn[1].add(id));
    segment.rightCodeIds.forEach((id) => appliedByColumn[3].add(id));
  });
  const result = [];
  [1, 3].forEach((gridColumn) => {
    appliedByColumn[gridColumn].forEach((codeId) => {
      if (!verdictCodesByColumn[gridColumn].has(codeId)) result.push({ gridColumn, codeId });
    });
  });
  return result;
};

// Per-column spans for the chip-hover recolor: the same DOM spans
// buildUnderlineSpans targets (same cut points — own excerpt boundaries plus
// the full diff's segment edges — so the CSS keeps hitting the rendered
// `.codeSpan[start]` nodes, whose splits don't change while a chip is
// hovered), but classified for ONE code instead of the whole code sets.
// Hovering a chip isolates that code, and with a single code in play
// 'partial' stops being a possible state — every piece of the code's text
// has a crisp answer about the OTHER coder:
//   'agree'    — they also applied this code to this text (their extra codes
//                here don't matter; those are hidden while hovering).
//   'conflict' — they coded this text, but never with this code.
//   'oneSided' — they left this text uncoded: coverage info, not a verdict,
//                same product decision as classifyDiffSegment.
// Only spans the column's own coder covered with the code are returned —
// everything else stays hidden under the native hover isolation.
// `diff` / `extraCuts`: same contract as buildUnderlineSpans below — a
// memoized canonical diff (edges are orientation-independent) and the active
// selection's boundaries.
export const buildHoverUnderlineSpans = (paragraph, ownExcerpts, otherExcerpts, codeId, diff = null, extraCuts = []) => {
  const bodyText = paragraph.bodyText || '';
  const bodyStart = paragraph.end - bodyText.length;
  const bodyEnd = paragraph.end;
  const overlapsBody = (excerpt) => !(excerpt.end <= bodyStart || excerpt.start >= bodyEnd);
  const ownRanges = excerptsToRanges((ownExcerpts || []).filter(overlapsBody), bodyStart, bodyEnd);
  if (!ownRanges.length) return [];
  const otherRanges = excerptsToRanges((otherExcerpts || []).filter(overlapsBody), bodyStart, bodyEnd);

  // Same id normalization as SentenceClass's containsPrime — the active code
  // id arrives from chip props and may be a string; codings' code_ids are
  // numeric.
  const targetId = Number(codeId);

  const { segments } = diff || buildParagraphDiff(paragraph, ownExcerpts, otherExcerpts);
  const pointSet = new Set(ownRanges.flatMap((r) => [r.start, r.end]));
  segments.forEach((segment) => { pointSet.add(segment.start); pointSet.add(segment.end); });
  (extraCuts || [])
    .map((cut) => cut - bodyStart)
    .filter((cut) => cut > 0 && cut < bodyText.length)
    .forEach((cut) => pointSet.add(cut));
  const points = [...pointSet].sort((a, b) => a - b);

  const spans = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    const mid = (start + end) / 2;
    const ownCodeIds = codeIdsCovering(ownRanges, mid);
    if (!ownCodeIds || !ownCodeIds.some((id) => Number(id) === targetId)) continue;
    const otherCodeIds = codeIdsCovering(otherRanges, mid);
    let classification = 'oneSided';
    if (otherCodeIds) {
      classification = otherCodeIds.some((id) => Number(id) === targetId) ? 'agree' : 'conflict';
    }
    // No coalescing of adjacent same-classification spans: each entry must
    // stay 1:1 with a rendered .codeSpan (keyed by its start offset), same as
    // buildUnderlineSpans.
    spans.push({ start: bodyStart + start, end: bodyStart + end, classification });
  }
  return spans;
};

// Tie-break severity for the straddle FALLBACK below (which should no longer
// occur — see buildUnderlineSpans): prefer surfacing the disagreement.
// Between agree and oneSided, stay neutral (gray) rather than overclaim
// agreement over text the other coder never touched.
const SEVERITY = { agree: 0, oneSided: 1, partial: 2, conflict: 3 };

// Per-column agreement spans for the underline recolor CSS, one entry per
// coded span the paragraph renderer actually draws.
//
// DOM contract this leans on: SentenceBuilder cuts a paragraph's body text
// at every one of the column's own excerpt boundaries (clipped to the body
// range) AND, in comparison mode, at the diff-segment edges ComparisonView
// passes down as extraBoundaries — the union of BOTH coders' boundaries.
// Each covered chunk renders as a single `.codeSpan` carrying its ABSOLUTE
// character offset in a `start` attribute. So decomposing the column's own
// excerpt ranges at their boundary points plus the segment edges reproduces
// exactly the coded spans in that column's DOM, and `start` is a stable CSS
// hook for each one.
//
// Each span is classified from the diff segments it covers — the same
// buildParagraphDiff segments the heatmap cells color by. Because the split
// points include every segment edge, each span lies entirely inside one
// segment and gets exactly that segment's classification — a one-sided
// slice in the middle of one coder's long excerpt renders gray even though
// its neighbors render agree/conflict. The dominant-length/severity fold
// below is kept only as a safe fallback should a span ever straddle
// segments again; with the shared split points it reduces to an exact
// single-segment lookup.
//
// `diff` (optional): the paragraph's already-computed buildParagraphDiff
// result, so callers holding a memoized diff don't pay for a rebuild. The
// segment EDGES and per-segment classifications are orientation-independent
// (classifyDiffSegment folds 'left'/'right' into 'oneSided'), so the same
// canonical (left, right) diff serves both columns.
//
// `extraCuts` (optional): additional ABSOLUTE offsets to cut the spans at.
// While a text selection is active, SentenceClass re-cuts the rendered spans
// at the selection's boundaries (see _createSelects) — passing those two
// offsets here keeps the emitted rules 1:1 with the re-cut DOM, so a span's
// unselected tail doesn't lose its agreement styling for the life of the
// selection.
export const buildUnderlineSpans = (paragraph, ownExcerpts, otherExcerpts, diff = null, extraCuts = []) => {
  const bodyText = paragraph.bodyText || '';
  const bodyStart = paragraph.end - bodyText.length;
  const bodyEnd = paragraph.end;
  const overlapsBody = (excerpt) => !(excerpt.end <= bodyStart || excerpt.start >= bodyEnd);
  const ownRanges = excerptsToRanges((ownExcerpts || []).filter(overlapsBody), bodyStart, bodyEnd);
  if (!ownRanges.length) return [];

  const { segments } = diff || buildParagraphDiff(paragraph, ownExcerpts, otherExcerpts);

  // Own excerpt boundaries plus every segment edge — the same cut points the
  // renderer uses (its own boundaries plus extraBoundaries), keeping the CSS
  // spans and the DOM spans 1:1.
  const pointSet = new Set(ownRanges.flatMap((r) => [r.start, r.end]));
  segments.forEach((segment) => { pointSet.add(segment.start); pointSet.add(segment.end); });
  (extraCuts || [])
    .map((cut) => cut - bodyStart)
    .filter((cut) => cut > 0 && cut < bodyText.length)
    .forEach((cut) => pointSet.add(cut));
  const points = [...pointSet].sort((a, b) => a - b);

  const spans = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    // Skip uncovered gaps between this column's excerpts.
    if (!codeIdsCovering(ownRanges, (start + end) / 2)) continue;

    const lengths = {};
    segments.forEach((segment) => {
      const overlap = Math.min(end, segment.end) - Math.max(start, segment.start);
      if (overlap <= 0) return;
      const classification = classifyDiffSegment(segment);
      if (!classification) return;
      lengths[classification] = (lengths[classification] || 0) + overlap;
    });

    const classification = Object.keys(lengths).sort(
      (a, b) => (lengths[b] - lengths[a]) || (SEVERITY[b] - SEVERITY[a])
    )[0];
    if (!classification) continue;

    spans.push({ start: bodyStart + start, end: bodyStart + end, classification });
  }
  return spans;
};

// ---------------------------------------------------------------------------
// Per-paragraph overlay cache
//
// One excerpt edit hands the page a NEW bulk excerpts array, but almost every
// paragraph's slice of it is unchanged. Recomputing the whole overlay anyway
// costs the math twice over — and, worse, mints fresh array identities for
// every paragraph's extraBoundaries/diff, which cascades through the memoized
// ParagraphContainers as a full-page re-render and SentenceBuilder rebuild.
//
// buildOverlayEntries recomputes only the paragraphs whose inputs actually
// changed, keyed by a content signature of the excerpts overlapping each
// paragraph's body (plus any active selection cuts). A cache hit returns the
// PREVIOUS entry object — same diff, same extraBoundaries array, same spans —
// so React's identity checks see unchanged paragraphs as unchanged. When no
// paragraph changed at all (e.g. the edit was on another page), the previous
// entries ARRAY comes back untouched and downstream memos skip entirely.

// The selection's boundaries as extra cut points, only when the selection
// overlaps the paragraph (elsewhere the renderer doesn't re-cut spans).
export const selectionCutsForParagraph = (paragraph, selection) =>
  selection && !(paragraph.start >= selection.end || paragraph.end <= selection.start)
    ? [selection.start, selection.end]
    : [];

const excerptsSignature = (excerpts) =>
  excerpts
    .map((e) => `${e.id}:${e.start}:${e.end}:${(e.codings || []).map((c) => `${c.code_id}.${c.user_id}`).join(',')}`)
    .join('|');

// Bucket excerpts by the paragraphs they overlap, via binary search over the
// sorted paragraph ranges — O(E log P + overlaps) instead of the O(P × E) of
// filtering the full excerpt list once per paragraph, which dominates the
// whole overlay build on long transcripts. Buckets use the paragraphs' FULL
// [start, end) ranges (a superset of the body range); the precise body
// overlap check runs per bucket, where the lists are tiny.
const bucketExcerptsByParagraph = (paragraphs, excerpts) => {
  const buckets = paragraphs.map(() => []);
  if (!excerpts || !excerpts.length || !paragraphs.length) return buckets;
  (excerpts || []).forEach((excerpt) => {
    // First paragraph whose end is past the excerpt's start.
    let lo = 0;
    let hi = paragraphs.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (paragraphs[mid].end <= excerpt.start) lo = mid + 1;
      else hi = mid;
    }
    for (let i = lo; i < paragraphs.length && paragraphs[i].start < excerpt.end; i++) {
      buckets[i].push(excerpt);
    }
  });
  return buckets;
};

export const createOverlayCache = () => ({ map: new Map(), entries: null, paragraphs: null });

// `selections`: { 1: {start,end}|null, 3: {start,end}|null } — the live text
// selection per grid column (see ComparisonView's columnSelections).
export const buildOverlayEntries = (cache, paragraphs, leftExcerpts, rightExcerpts, selections = {}) => {
  const nextMap = new Map();
  let reused = 0;

  const leftBuckets = bucketExcerptsByParagraph(paragraphs, leftExcerpts);
  const rightBuckets = bucketExcerptsByParagraph(paragraphs, rightExcerpts);

  const entries = paragraphs.map((paragraph, index) => {
    const bodyStart = paragraph.end - (paragraph.bodyText || '').length;
    const overlapsBody = (excerpt) => !(excerpt.end <= bodyStart || excerpt.start >= paragraph.end);
    const leftOverlap = leftBuckets[index].filter(overlapsBody);
    const rightOverlap = rightBuckets[index].filter(overlapsBody);
    const cuts1 = selectionCutsForParagraph(paragraph, selections[1]);
    const cuts3 = selectionCutsForParagraph(paragraph, selections[3]);
    const key = `${paragraph.start}:${paragraph.end}`;
    const signature =
      `${excerptsSignature(leftOverlap)}~${excerptsSignature(rightOverlap)}~${cuts1.join(',')}~${cuts3.join(',')}`;

    const cached = cache.map.get(key);
    if (cached && cached.signature === signature) {
      nextMap.set(key, cached);
      reused += 1;
      return cached;
    }

    const diff = buildParagraphDiff(paragraph, leftOverlap, rightOverlap);
    const segments = diff.segments || [];
    const boundaryPoints = new Set();
    segments.forEach((segment) => {
      boundaryPoints.add(bodyStart + segment.start);
      boundaryPoints.add(bodyStart + segment.end);
    });
    const verdicts = buildChipVerdicts(diff);
    const entry = {
      signature,
      diff,
      // The diff-segment edges as absolute offsets — SentenceBuilder's
      // extraBoundaries (see ComparisonView).
      extraBoundaries: [...boundaryPoints].sort((a, b) => a - b),
      underlineSpans1: buildUnderlineSpans(paragraph, leftOverlap, rightOverlap, diff, cuts1),
      underlineSpans3: buildUnderlineSpans(paragraph, rightOverlap, leftOverlap, diff, cuts3),
      verdicts,
      oneSidedChipCodes: buildOneSidedChipCodes(diff, verdicts),
      tooltipSegments: buildTooltipSegments(paragraph, diff),
    };
    nextMap.set(key, entry);
    return entry;
  });

  const unchanged =
    cache.entries &&
    cache.paragraphs === paragraphs &&
    cache.entries.length === entries.length &&
    entries.every((entry, i) => entry === cache.entries[i]);

  cache.map = nextMap;
  cache.paragraphs = paragraphs;
  cache.entries = unchanged ? cache.entries : entries;
  cache.lastReusedCount = reused; // observability for tests/benchmarks
  return cache.entries;
};
